/**
 * Éditeur d'horaires PAR MAGASIN (direction) — étend la source globale existante.
 * - Hebdomadaire : par jour, « Défaut (global) » / « Fermé » / « Ouvert [o]–[c] ».
 *   « Défaut » retire le jour de l'objet -> l'horaire GLOBAL (HoursLive.OPENING_HOURS) s'applique.
 * - Exceptions par DATE : liste {date -> Fermé | [o,c]}. Aucune date fériée pré-remplie.
 * Sortie : horaires = {dow: [o,c]|null} (jours personnalisés uniquement), exceptions = {YYYY-MM-DD: [o,c]|null}.
 */
import React from 'react';
import { Plus, Trash2, Clock } from 'lucide-react';
import { OPENING_HOURS } from '../landing/HoursLive';

const DAYS = [
  { dow: 1, label: 'Lundi' }, { dow: 2, label: 'Mardi' }, { dow: 3, label: 'Mercredi' },
  { dow: 4, label: 'Jeudi' }, { dow: 5, label: 'Vendredi' }, { dow: 6, label: 'Samedi' }, { dow: 0, label: 'Dimanche' },
];
const HOURS = Array.from({ length: 25 }, (_, i) => i); // 0..24

const defaultLabel = (dow) => {
  const g = OPENING_HOURS[dow];
  return g ? `${g[0]}h–${g[1]}h` : 'fermé';
};
const modeOf = (val) => (val === undefined ? 'default' : (val === null ? 'closed' : 'open'));

const HourSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))}
    className="px-1.5 py-1 rounded bg-black/30 border border-white/10 text-white text-xs font-mono" data-testid={testid}>
    {HOURS.map((h) => <option key={h} value={h}>{h}h</option>)}
  </select>
);

const MagasinHoursEditor = ({ horaires, exceptions, onChange, code }) => {
  const h = horaires || {};
  const exc = exceptions || {};

  const setDay = (dow, val) => {
    const next = { ...h };
    if (val === undefined) delete next[dow]; else next[dow] = val;
    onChange({ horaires: next, exceptions: exc });
  };
  const setExc = (nextExc) => onChange({ horaires: h, exceptions: nextExc });

  return (
    <section className="space-y-3" data-testid={`magasin-hours-editor-${code || ''}`}>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-300" />
        <h3 className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Horaires du magasin (rappel de fermeture)</h3>
      </div>
      <p className="text-[10px] text-gray-500">Non renseigné = horaire <strong>global par défaut</strong>. Sert au rappel « fermer la caisse » après l'heure de fermeture.</p>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] divide-y divide-white/5">
        {DAYS.map(({ dow, label }) => {
          const val = h[dow];
          const mode = modeOf(val);
          const open = mode === 'open' ? val[0] : (OPENING_HOURS[dow] ? OPENING_HOURS[dow][0] : 9);
          const close = mode === 'open' ? val[1] : (OPENING_HOURS[dow] ? OPENING_HOURS[dow][1] : 19);
          return (
            <div key={dow} className="flex items-center gap-2 p-2" data-testid={`hours-day-${dow}`}>
              <span className="w-20 text-xs text-gray-300">{label}</span>
              <select value={mode}
                onChange={(e) => {
                  const m = e.target.value;
                  if (m === 'default') setDay(dow, undefined);
                  else if (m === 'closed') setDay(dow, null);
                  else setDay(dow, [open, close]);
                }}
                className="px-2 py-1 rounded bg-black/30 border border-white/10 text-white text-xs" data-testid={`hours-mode-${dow}`}>
                <option value="default">Défaut ({defaultLabel(dow)})</option>
                <option value="closed">Fermé</option>
                <option value="open">Ouvert…</option>
              </select>
              {mode === 'open' && (
                <div className="flex items-center gap-1">
                  <HourSelect value={open} onChange={(v) => setDay(dow, [v, close])} testid={`hours-open-${dow}`} />
                  <span className="text-gray-500 text-xs">→</span>
                  <HourSelect value={close} onChange={(v) => setDay(dow, [open, v])} testid={`hours-close-${dow}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Exceptions par date */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase text-gray-500 font-semibold">Exceptions (dates)</span>
          <button type="button"
            onClick={() => setExc({ ...exc, '': null })}
            className="text-[11px] px-2 py-1 rounded border border-white/10 bg-black/20 text-gray-300 hover:text-white inline-flex items-center gap-1"
            data-testid="hours-exc-add">
            <Plus className="w-3 h-3" /> Ajouter une date
          </button>
        </div>
        {Object.keys(exc).length === 0 && <p className="text-[10px] text-gray-600">Aucune exception. (ex. jour férié fermé, ou horaire réduit un jour précis.)</p>}
        {Object.entries(exc).map(([date, val], i) => {
          const mode = val === null ? 'closed' : 'open';
          const o = mode === 'open' ? val[0] : 9;
          const c = mode === 'open' ? val[1] : 12;
          const rename = (newDate) => { const n = { ...exc }; delete n[date]; n[newDate] = val; setExc(n); };
          const setVal = (v) => setExc({ ...exc, [date]: v });
          const remove = () => { const n = { ...exc }; delete n[date]; setExc(n); };
          return (
            <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`hours-exc-row-${i}`}>
              <input type="date" value={date} onChange={(e) => rename(e.target.value)}
                className="px-2 py-1 rounded bg-black/30 border border-white/10 text-white text-xs" data-testid={`hours-exc-date-${i}`} />
              <select value={mode} onChange={(e) => setVal(e.target.value === 'closed' ? null : [o, c])}
                className="px-2 py-1 rounded bg-black/30 border border-white/10 text-white text-xs" data-testid={`hours-exc-mode-${i}`}>
                <option value="closed">Fermé</option>
                <option value="open">Ouvert…</option>
              </select>
              {mode === 'open' && (
                <div className="flex items-center gap-1">
                  <HourSelect value={o} onChange={(v) => setVal([v, c])} testid={`hours-exc-open-${i}`} />
                  <span className="text-gray-500 text-xs">→</span>
                  <HourSelect value={c} onChange={(v) => setVal([o, v])} testid={`hours-exc-close-${i}`} />
                </div>
              )}
              <button type="button" onClick={remove} className="p-1 rounded text-red-300 hover:bg-red-500/10" data-testid={`hours-exc-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MagasinHoursEditor;
